/**
 * Turning a stored conversation back into the turns this surface draws.
 *
 * The models-list chat minted a thread on every mount, so a reload threw the
 * conversation away: on 2026-09-22 a reader who refreshed the page came back to an
 * empty panel with the work they had just done nowhere on screen. rgip has kept the
 * thread the whole time, per tenant and per user, so nothing new is stored to fix
 * this - it is only a matter of asking for what is already there.
 *
 * Here rather than in the component because this repo has no React testing
 * utilities, so a mapping written inside a component is a mapping nothing can check.
 */
import { ChatMessage, UiActionEvent } from '../model/rgipChatApi';

export interface HomeTurn {
  role: 'user' | 'agent';
  text: string;
  jumps?: UiActionEvent[];
}

/**
 * The stored messages as turns, or an empty list when there is nothing to show.
 *
 * Two differences from a live conversation, both deliberate.
 *
 * `role` is renamed: rgip stores `assistant` and this surface has always drawn
 * `agent`, and a replayed turn that keeps the storage spelling renders as neither
 * side of the conversation.
 *
 * Choice buttons come back through `pendingChoiceKey` below, which is a separate step
 * because it needs two fetches this mapping cannot make.
 */
export function replayHomeTurns(messages: ChatMessage[]): HomeTurn[] {
  if (!Array.isArray(messages)) return [];
  return messages
    .filter((message) => message && typeof message.content === 'string')
    .map((message) => ({
      role: message.role === 'user' ? ('user' as const) : ('agent' as const),
      text: message.content,
      ...(message.actions?.length ? { jumps: message.actions } : {}),
    }))
    .filter((turn) => turn.text.trim().length > 0);
}

/**
 * The stored block holding the decision a conversation ENDED on, if it ended on one.
 *
 * Only the last assistant turn is asked about, because only the last turn's buttons
 * are ever drawn: a decision belongs to the turn that offered it, and once the
 * conversation moved past it the decision was made. That makes this one lookup rather
 * than one per turn, and it is the same rule the live panel renders by.
 *
 * rgip strips a deliverable block out of the stored prose and serves its body through
 * the deliverables API, so the transcript carries only a reference. This returns the
 * key to resolve; the caller does the resolving, because that is two requests and this
 * file is the part worth testing.
 */
export function pendingChoiceKey(messages: ChatMessage[]): string | null {
  if (!Array.isArray(messages)) return null;
  const last = [...messages]
    .reverse()
    .find((message) => message?.role === 'assistant');
  const ref = last?.deliverables?.find((d) => d?.key === CHOICES_KEY);
  return ref?.key ?? null;
}

/** The one block key this surface renders. rgip calls it the same thing. */
export const CHOICES_KEY = 'choices';
